import './index.scss';
import React, {forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState} from "react";
import {Col} from "reactstrap";
import Dialog from "../../base/dialog";
import Button from "../../base/button/button";
import Form, {MuiRFF, Yup} from "../../form";
import ValidateMessages from "../../../../core/models/static/texts/vallidate-messages";
import useIsMounted from "../../../hooks/use-is-mounted";
import {Box, Card, CircularProgress, DialogActions, DialogContent, DialogTitle, IconButton, MenuItem, Skeleton, Tooltip, Typography, Unstable_Grid2 as Grid} from "@mui/material";
import {ProductTechnicalPropertyTypeIds} from "../../../../core/constants/enums";
import {ReactComponent as CheckboxIcon} from "../../../../assets/images/check-box/checkbox.svg";
import {ReactComponent as ActiveCheckboxIcon} from "../../../../assets/images/check-box/checkbox-active.svg";
import {DeleteOutlined, EditOutlined} from "@mui/icons-material";
import classnames from "classnames";
import SaveOrRollback from "../../app-specific/save-or-rollback";
import Utils, {createUUId} from "../../../../core/services/utils/utils";
import Api from "../../../../core/services/api/api";
import {setReduxDialogState} from "../../../redux/slices/dialogs/actions";
import ReduxStoreRoutes from "../../../../core/models/static/routes/redux-store-routes";
import {useDispatch} from "react-redux";
import {Checkbox} from "../../base/checkbox/checkbox";
// @ts-ignore
import {ReactComponent as Icon} from "../../../../assets/images/part-support-request/checkbox.svg";
// @ts-ignore
import {ReactComponent as IconCheckbox} from "../../../../assets/images/part-support-request/checkbox-active.svg";


const formNames = {
    title: 'title',
    typeId: 'typeId',
    description: 'description',
    hasPredefinedValues: 'hasPredefinedValues',
    isMultiSelect: 'isMultiSelect',
}


// TODO: replace by translations (object selection)
const ProductTechnicalPropertyTypeNames = {
    [ProductTechnicalPropertyTypeIds.text]: "Text",
    [ProductTechnicalPropertyTypeIds.number]: "Number",
    [ProductTechnicalPropertyTypeIds.decimal]: "Decimal",
    [ProductTechnicalPropertyTypeIds.fraction]: "Fraction",
    [ProductTechnicalPropertyTypeIds.money]: "Currency",
    [ProductTechnicalPropertyTypeIds.boolean]: "True / False",
    [ProductTechnicalPropertyTypeIds.date]: "Date",
    [ProductTechnicalPropertyTypeIds.color]: "Color",
}

const UpsertProductTechnicalPropertyDialog = ({open, property, onUpsert, onClose, industryId}) => {
    const [submitting, setSubmitting] = useState(false);
    const isMounted = useIsMounted();
    const optionsRef = useRef(null);
    const formRef = useRef(null);
    const formId = useMemo(() => createUUId(), []);

    const isInUpdateMode = useMemo(() => !!property, [property]);
    const isDefault = useMemo(() => property?.isDefault, [property?.isDefault]);

    const schema = useMemo(() => Yup.object().shape({
        [formNames.title]: isDefault ? Yup.string().nullable() : Yup.string().nullable().required(ValidateMessages.required),
        [formNames.typeId]: isDefault ? Yup.number().nullable() : Yup.number().nullable().required(ValidateMessages.required),
        [formNames.description]: isDefault ? Yup.string().nullable() : Yup.string().nullable().trim(),
        ...(isInUpdateMode && {
            [formNames.hasPredefinedValues]: Yup.boolean().default(false).nullable(),
            [formNames.isMultiSelect]: Yup.boolean().default(false).nullable(),
        }),
    }), [isInUpdateMode, isDefault]);

    const validate = useMemo(() => MuiRFF.makeValidate(schema), [schema]);
    const required = useMemo(() => MuiRFF.makeRequired(schema), [schema]);

    const initialValues = useMemo(() => ({
        [formNames.title]: property?.title,
        [formNames.typeId]: property?.typeId ?? property?.industryPropertyTypeId,
        [formNames.description]: property?.description,
        ...(isInUpdateMode && {
            [formNames.hasPredefinedValues]: property?.hasPredefinedValues,
            [formNames.isMultiSelect]: property?.isMultiSelect,
        }),
    }), [property, isInUpdateMode]);

    /**
     * Creates a category with the provided values.
     * @type {(function(*): Promise<undefined|{[formNames.coverImage]: *}>)}
     */
    const createProperty = useCallback(async (values) => {
        const typeId = Number(values[formNames.typeId]);
        const forApi = {
            title: values[formNames.title],
            industryPropertyTypeId: typeId,
            description: values[formNames.description],
            industryId: industryId,
        }
        setSubmitting(true);
        const response = await Api.createCustomIndustryProperty(forApi);
        if (!isMounted())
            return;
        setSubmitting(false);
        if (response.resultFlag) {
            onUpsert({
                id: response.data.id,
                title: forApi.title,
                description: forApi.description,
                typeId: forApi.industryPropertyTypeId,
                hasPredefinedValues: false,
                isDefault: false,
                isMultiSelect: false,
                value: null,
                selectedOptions: [],
            })
            onClose();
        }
    }, [isMounted, onClose, onUpsert, industryId])

    /**
     * Updates the category with the provided values.
     * @type {(function(*): Promise<void>)}
     */
    const updateProperty = useCallback(async (values) => {
        if (
            !isDefault &&
            !(
                values[formNames.title] === property.title &&
                values[formNames.typeId] === property.typeId &&
                values[formNames.description] === property.description &&
                values[formNames.hasPredefinedValues] === property.hasPredefinedValues &&
                values[formNames.isMultiSelect] === property.isMultiSelect
            )
        ) {
            const forApi = {
                id: property?.id,
                title: values[formNames.title],
                industryPropertyTypeId: Number(values[formNames.typeId]),
                description: values[formNames.description],
                updatedProperties: [
                    ...(!isDefault ? [
                        'Title',
                        'IndustryPropertyTypeId',
                        'Description',
                    ] : []),
                ],
            }
            setSubmitting(true);
            const response = await Api.updateCustomIndustryProperty(forApi);
            if (!isMounted())
                return;
            setSubmitting(false);
            if (response?.resultFlag) {
                onUpsert({
                    id: forApi.id,
                    title: forApi.title,
                    typeId: forApi.industryPropertyTypeId,
                    description: forApi.description,
                })
            }
            if (!(
                values[formNames.hasPredefinedValues] === property.hasPredefinedValues &&
                values[formNames.isMultiSelect] === property.isMultiSelect
            )) {
                const response = await Api.setIndustryPropertyConfig({
                    id: property?.id,
                    hasPreDefinedValues: values.hasPredefinedValues,
                    isMultiSelect: values.isMultiSelect,
                })
                if (!isMounted())
                    return;
                setSubmitting(false);
                if (!response?.resultFlag) {
                    return;
                }
                onUpsert({
                    id: property?.id,
                    hasPredefinedValues: values.hasPredefinedValues,
                    isMultiSelect: values.isMultiSelect,
                })
            }
        }
        if (property?.hasPredefinedValues && optionsRef.current) {
            const options = await new Promise(async (r) => {
                optionsRef.current.onSubmitted((options) => r(options))
                await optionsRef.current.submit();
            })
            if (!Array.isArray(options)) {
                return;
            }
            onUpsert({
                id: property?.id,
                selectedOptions: options,
            })
        }
        onClose();
    }, [isMounted, isDefault, onClose, onUpsert, property?.description, property?.hasPredefinedValues, property?.id, property?.isMultiSelect, property?.title, property?.typeId])

    /**
     * Create a new category and close the dialog
     */
    const submitForm = useCallback(async (values) => {
        if (isDefault) {
            return onClose();
        }
        if (property) {
            return await updateProperty(values);
        } else {
            return await createProperty(values);
        }
    }, [createProperty, isDefault, onClose, property, updateProperty])

    /**
     * If the user presses enter, and the new option is enabled, then add the next option.
     */
    const onFormKeyDown = useCallback((e) => {
        if (e.key === 'Enter') {
            if (e.target.closest('form')?.id !== formId) {
                e.preventDefault();
            } else {
                formRef.current?.submit();
            }
        }
    }, [formId])

    return (
        <Dialog
            open={open}
            setOpen={onClose}
            maxWidth={'sm'}
        >
            <DialogTitle id={'scroll-dialog-title'}>
                <p className={`text-lg text-color-1 font-weight-bold text-center`}>
                    {
                        isDefault
                            ? "View Property"
                            : property
                                ? "Edit Property"
                                : "Create New Property"
                    }
                </p>
            </DialogTitle>
            <DialogContent className={'px-4'} dividers>
                <Form
                    formProps={{
                        id: formId,
                        onKeyDown: onFormKeyDown,
                        className: 'd-flex flex-column w-100 mt-3 upsert-product-technical-property-form',
                        style: isDefault ? {gap: 12} : {},
                    }}
                    onSubmit={submitForm}
                    validate={validate}
                    initialValues={initialValues}
                >
                    {({form, values}) => {
                        formRef.current = form;
                        return (
                            <>
                                <Col xs={12} className={classnames('px-0 d-flex', {'mb-3': !isDefault})}>
                                    {
                                        isDefault &&
                                        <Typography variant={'body1'} fontWeight={600} sx={{marginBlockStart: '7px'}}>
                                            Title:
                                        </Typography>
                                    }
                                    <MuiRFF.TextField
                                        name={formNames.title}
                                        label={'Title'}
                                        required={required[formNames.title]}
                                        disabled={submitting}
                                        className={classnames({'show-as-text': isDefault})}
                                    />
                                </Col>
                                <Col xs={12} className={classnames('px-0 d-flex', {'mb-3': !isDefault})}>
                                    {
                                        isDefault &&
                                        <Typography variant={'body2'} fontWeight={600} sx={{marginBlockStart: '7px'}}>
                                            Description:
                                        </Typography>
                                    }
                                    <MuiRFF.TextField
                                        multiline
                                        {...(!isDefault && {minRows: 3})}
                                        {...(isDefault && {value: values[formNames.description] ?? '--'})}
                                        name={formNames.description}
                                        label={'Description'}
                                        required={required[formNames.description]}
                                        disabled={submitting}
                                        className={classnames({'show-as-text': isDefault})}
                                    />
                                </Col>
                                <Col xs={12} className={classnames('px-0 d-flex', {'mb-3': !isDefault})}>
                                    {
                                        (isDefault || isInUpdateMode) &&
                                        <Typography variant={'body2'} fontWeight={600} sx={{marginBlockStart: '7px'}}>
                                            Type:
                                        </Typography>
                                    }
                                    <MuiRFF.TextField
                                        select
                                        fullWidth
                                        disabled={submitting}
                                        name={formNames.typeId}
                                        label={"Type"}
                                        required={required[formNames.typeId]}
                                        className={classnames({'show-as-text': isDefault || isInUpdateMode})}
                                    >
                                        {
                                            Object.entries(ProductTechnicalPropertyTypeNames).map(([value, label]) => (
                                                <MenuItem value={value} key={value}>
                                                    {label}
                                                </MenuItem>
                                            ))
                                        }
                                    </MuiRFF.TextField>
                                </Col>
                                {
                                    isInUpdateMode &&
                                    (
                                        values[formNames.typeId] !== ProductTechnicalPropertyTypeIds.color &&
                                        values[formNames.typeId] !== ProductTechnicalPropertyTypeIds.boolean &&
                                        values[formNames.typeId] !== ProductTechnicalPropertyTypeIds.date
                                    ) &&
                                    <>
                                        <Col xs={12} className={'mb-3 px-0 d-flex flex-wrap'} style={{gap: 12}}>
                                            {
                                                isDefault &&
                                                <Tooltip
                                                    title={"Read Only"}
                                                    className={'cursor-not-allowed-hover'}
                                                    disableInteractive
                                                >
                                                    <span>
                                                        {/*@ts-ignore*/}
                                                        <Checkbox
                                                            className={'cursor-not-allowed-hover'}
                                                            labelClassName={'cursor-not-allowed-hover'}
                                                            value={true}
                                                            label={'Has Predefined Values'}
                                                            checked={values[formNames.hasPredefinedValues] ?? false}
                                                            onChange={() => false}
                                                            icon={<Icon/>}
                                                            checkedIcon={<IconCheckbox/>}
                                                        />
                                                    </span>
                                                </Tooltip>
                                            }
                                            <MuiRFF.Checkboxes
                                                color={'primary'}
                                                icon={<CheckboxIcon/>}
                                                checkedIcon={<ActiveCheckboxIcon/>}
                                                data={{value: values[formNames.hasPredefinedValues], label: 'Has Predefined Values'}}
                                                name={formNames.hasPredefinedValues}
                                                onChange={(e, value) => {
                                                    form.change(formNames.hasPredefinedValues, value)
                                                    if (!value) {
                                                        form.change(formNames.isMultiSelect, false)
                                                    }
                                                }}
                                                disabled={submitting}
                                                required={required[formNames.hasPredefinedValues]}
                                                sx={{marginBlockEnd: 0, marginInlineEnd: 0,}}
                                                formControlProps={{
                                                    sx: {...(isDefault ? {display: 'none'} : {})},
                                                }}
                                                className={classnames({'show-as-text': isDefault})}
                                            />
                                            {
                                                values[formNames.hasPredefinedValues] &&
                                                <>
                                                    {
                                                        isDefault &&
                                                        <Tooltip
                                                            title={"Read Only"}
                                                            className={'cursor-not-allowed-hover'}
                                                            disableInteractive
                                                        >
                                                            <span>
                                                                {/*@ts-ignore*/}
                                                                <Checkbox
                                                                    className={'cursor-not-allowed-hover'}
                                                                    labelClassName={'cursor-not-allowed-hover'}
                                                                    value={true}
                                                                    label={'Multi-select'}
                                                                    checked={values.isMultiSelect ?? false}
                                                                    onChange={() => false}
                                                                    icon={<Icon/>}
                                                                    checkedIcon={<IconCheckbox/>}
                                                                />
                                                            </span>
                                                        </Tooltip>
                                                    }
                                                    <MuiRFF.Checkboxes
                                                        color={'primary'}
                                                        icon={<CheckboxIcon/>}
                                                        checkedIcon={<ActiveCheckboxIcon/>}
                                                        data={{value: values[formNames.isMultiSelect], label: 'Multi-select'}}
                                                        name={formNames.isMultiSelect}
                                                        disabled={submitting}
                                                        required={required[formNames.isMultiSelect]}
                                                        sx={{marginBlockEnd: 0, marginInlineEnd: 0,}}
                                                        formControlProps={{
                                                            sx: {...(isDefault ? {display: 'none'} : {})},
                                                        }}
                                                        className={classnames({'show-as-text': isDefault})}
                                                    />
                                                </>
                                            }
                                        </Col>
                                        {
                                            values[formNames.hasPredefinedValues] && property?.hasPredefinedValues &&
                                            <UpsertProductTechnicalPropertyDialogOptions
                                                ref={optionsRef}
                                                propertyId={property.id}
                                                propertyTypeId={property.typeId}
                                            />
                                        }
                                    </>
                                }
                                {
                                    isInUpdateMode && (
                                        values[formNames.typeId] === ProductTechnicalPropertyTypeIds.color ||
                                        values[formNames.typeId] === ProductTechnicalPropertyTypeIds.boolean ||
                                        values[formNames.typeId] === ProductTechnicalPropertyTypeIds.date
                                    ) &&
                                    <Typography variant={'body2'} color={'textSecondary'} textAlign={'center'}>
                                        {
                                            values[formNames.typeId] === ProductTechnicalPropertyTypeIds.color
                                                ? "Color properties do not support predefined values."
                                                : values[formNames.typeId] === ProductTechnicalPropertyTypeIds.boolean
                                                    ? "True/False properties do not support predefined values."
                                                    : "Date properties do not support predefined values."
                                        }
                                    </Typography>
                                }
                            </>
                        )
                    }}
                </Form>
            </DialogContent>
            <DialogActions className={'p-0 w-100 d-flex justify-content-between'}>
                <Button
                    disabled={!open || submitting}
                    variant={'dialog'}
                    color={'primary'}
                    type={'button'}
                    onClick={() => onClose(false)}
                    borderRadius={'0 8px 0 8px'}
                >
                    {submitting ? 'submitting...' : 'Close'}
                </Button>
                {
                    !isDefault &&
                    <Button
                        disabled={!open || submitting}
                        variant={'dialog'}
                        color={'primary'}
                        type={'button'}
                        onClick={() => formRef.current?.submit()}
                        borderRadius={'8px 0 8px 0'}
                    >
                        {submitting ? 'submitting...' : isInUpdateMode ? 'Update' : 'Create'}
                    </Button>
                }
            </DialogActions>
        </Dialog>
    );
}


const UpsertProductTechnicalPropertyDialogOptions = React.forwardRef(({
                                                                          propertyId,
                                                                          propertyTypeId,
                                                                      }, ref) => {
    const [options, _setOptions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [newOption, setNewOption] = useState();
    const isMounted = useIsMounted();
    const onSubmittedRef = useRef(null);
    const optionsListRef = useRef([]);
    const optionsRef = useRef({});
    const dispatch = useDispatch();

    /**
     * Sets the options
     * @type {(function(*): void)}
     */
    const setOptions = useCallback((optionsOrCallback) => {
        _setOptions(prevState => {
            const newState = typeof optionsOrCallback === 'function' ? optionsOrCallback(prevState) : optionsOrCallback;
            optionsListRef.current = newState;
            return newState;
        })
    }, [])


    /**
     * Submits all the options and awaits their result. Calls the onSubmittedRef afterwards
     * @type {(function(): Promise<boolean|undefined>)|*}
     */
    const submit = useCallback(async () => {
        const results = await Promise.all(Object.values(optionsRef.current).map(ref => ref.submit()))
        if (results.some(r => !r)) {
            return false;
        }
        setTimeout(() => {
            onSubmittedRef.current?.(optionsListRef.current);
        }, 300);
    }, [])

    const refValues = useMemo(() => ({
        submit: submit,
        onSubmitted: (callback) => {
            onSubmittedRef.current = callback;
        },
    }), [submit])

    /**
     * Exposes the ref values to the parent component.
     */
    useImperativeHandle(ref, () => refValues, [refValues])

    /**
     * Fetches the options for the property.
     * @type {(function(): Promise<void>)}
     */
    const getOptions = useCallback(async () => {
        setLoading(true);
        const response = await Api.listIndustryPropertyPropertyValues(propertyId)
        if (!isMounted())
            return;
        setLoading(false);
        if (response?.resultFlag) {
            setOptions(response.data?.map(e => ({id: e.id, title: e.content, isDefault: e.isDefault})) ?? [])
        }
    }, [isMounted, propertyId, setOptions]);

    /**
     * As soon as the component mounts, fetch the options.
     */
    useEffect(() => {
        void getOptions();
    }, [getOptions]);

    /**
     * Adds a new option to the form.
     */
    const addNewOption = useCallback(() => {
        setNewOption({
            id: Number.MAX_SAFE_INTEGER,
            title: '',
            inEditMode: true,
            editing: false,
            removing: false,
        })
    }, [])

    /**
     * Creates a new option.
     */
    const createOption = useCallback(async (option, createNextRow) => {
        setNewOption(prevState => ({...prevState, editing: true}))
        const response = await Api.addIndustryPropertyPropertyValue({
            content: option.title,
            industryPropertyId: propertyId,
        })
        if (!isMounted())
            return;
        if (response?.resultFlag && !!response.data) {
            setOptions(prevState => [
                ...prevState,
                {
                    ...option,
                    id: response.data.id,
                    inEditMode: false,
                    editing: false,
                    removing: false,
                }
            ])
            if (createNextRow) {
                addNewOption();
            } else {
                setNewOption(undefined);
            }
        } else {
            setNewOption(prevState => ({...prevState, editing: false}))
        }
        return response?.resultFlag;
    }, [addNewOption, isMounted, propertyId, setOptions])

    /**
     * Updates the option with the given changes.
     */
    const updateOption = useCallback(async (changes) => {
        const forApi = {
            id: changes.id,
            content: "",
            updatedProperties: [],
        }
        if ('title' in changes) {
            forApi.content = changes.title;
            forApi.updatedProperties.push('Content');
        }
        setOptions(prevState => prevState.map(o => o.id === changes.id ? {...o, editing: true} : o));
        const response = await Api.updateIndustryPropertyPropertyValue(forApi);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setOptions(prevState => prevState.map(o => o.id === changes.id ? {...o, ...changes, inEditMode: false} : o));
        }
        setOptions(prevState => prevState.map(o => o.id === changes.id ? {...o, editing: false} : o));
        return response?.resultFlag;
    }, [isMounted, setOptions])

    /**
     * Deletes the option with the given id.
     */
    const deleteOption = useCallback(async (optionId) => {
        const option = options.find(o => o.id === optionId);
        dispatch(setReduxDialogState(ReduxStoreRoutes.dialogs.removeConfirmation, {
            open: true,
            data: options
                .filter(e => e.id !== optionId)
                .map(e => ({
                    title: e.title,
                    id: e.id,
                    data: e,
                })),
            removedEntryTitle: `property option: "${option.title}`,
            onClose: async (confirmed, data) => {
                if (!confirmed)
                    return;
                if (!data)
                    return true;
                setOptions(prevState => prevState.map(o => o.id === optionId ? {...o, removing: true} : o));
                const response = await Api.deleteIndustryPropertyPropertyValue({
                    id: optionId,
                    replacementId: data.id,
                })
                if (!isMounted()) {
                    return true;
                }
                if (response?.resultFlag) {
                    setOptions(prevState => prevState.filter(o => o.id !== optionId));
                } else {
                    setOptions(prevState => prevState.map(o => o.id === optionId ? {...o, removing: false} : o));
                }
                return response?.resultFlag;
            }
        }));
    }, [options, dispatch, setOptions, isMounted])

    /**
     * Toggles the edit mode of the entry.
     */
    const toggleOptionInEditMode = useCallback((optionId, inEditMode = false) => {
        setOptions(prevState => prevState.map(o => o.id === optionId ? {...o, inEditMode} : o));
    }, [setOptions])

    /**
     * Syncs the refs with the options.
     */
    useEffect(() => {
        const refKeys = Object.keys(optionsRef.current);
        const newOptionRefs = {};
        const optionIds = options.map(o => o.id);
        for (const key of refKeys) {
            if (optionIds.includes(key)) {
                newOptionRefs[key] = optionsRef.current[key];
            }
        }
        if (newOption) {
            newOptionRefs['new-option'] = optionsRef.current['new-option'];
        }
        optionsRef.current = newOptionRefs;
    }, [newOption, options])

    return (
        <>
            <div className={'d-flex flex-column w-100'} style={{gap: 12}}>
                <Typography variant={'caption'} fontWeight={600}>
                    Options
                </Typography>
                <div className={'d-flex flex-column'} style={{gap: 12}}>
                    {
                        loading &&
                        Array.from({length: 3}).map((_, index) => (
                            <Skeleton key={`loading-${index}`} height={40} sx={{transform: 'none'}}/>
                        ))
                    }
                    {
                        options.map((option, index, array) => (
                            <ProductPropertyOption
                                typeId={propertyTypeId}
                                key={option.id}
                                option={option}
                                update={updateOption}
                                delete={deleteOption}
                                removable={array.length > 1}
                                toggleInEditMode={toggleOptionInEditMode}
                                ref={ref => {
                                    optionsRef.current[option.id] = ref
                                }}
                            />
                        ))
                    }
                    {
                        !!newOption &&
                        <ProductPropertyOption
                            typeId={propertyTypeId}
                            newOption
                            option={newOption}
                            update={createOption}
                            delete={() => Promise.resolve(void 0)}
                            removable={false}
                            toggleInEditMode={() => void 0}
                            rollback={() => setNewOption(undefined)}
                            ref={ref => {
                                optionsRef.current['new-option'] = ref
                            }}
                        />
                    }
                    {
                        !newOption &&
                        <Button
                            size={'medium'}
                            variant={'outlined'}
                            onClick={addNewOption}
                        >
                            Add New Option
                        </Button>
                    }
                </div>
            </div>
        </>
    );
})


const ProductPropertyOption = forwardRef(({
                                              update,
                                              typeId,
                                              delete: remove,
                                              toggleInEditMode,
                                              removable,
                                              rollback,
                                              option,
                                              newOption
                                          }, ref) => {
    const id = useMemo(() => createUUId(), []);
    const addNextOption = useRef(false);
    const formRef = useRef(null);
    const inputRef = useRef(null);
    const readonly = useMemo(() => option?.isDefault ?? false, [option?.isDefault]);

    /**
     * Exposes the ref values to the parent component.
     */
    useImperativeHandle(ref, () => ({
        submit: async () => {
            if (!option?.inEditMode) {
                return true;
            }
            const errors = await formRef.current?.submit()
            return !errors;
        },
    }), [option?.inEditMode]);

    const validate = useCallback((values) => {
        const normalized = Utils.normalizeIndustryPropertyPredefinedOptionContent(
            typeId,
            values?.title?.toString?.() ?? '',
        );
        if (!normalized.valid) {
            return {title: normalized.message};
        }
        return {};
    }, [typeId]);
    const required = useMemo(() => ({title: true}), []);

    const initialValues = useMemo(() => {
        let title;
        switch (typeId) {
            case ProductTechnicalPropertyTypeIds.fraction:
                const fraction = Utils.technicalPropertyDecimalToFraction(option?.title);
                title = fraction ?? "";
                break;
            default:
                title = option?.title?.toString() ?? "";
                break;
        }
        return ({
            title: title,
        })
    }, [option?.title, typeId])

    /**
     * Removes the entry by calling the appropriate callback in the props.
     */
    const removeEntry = useCallback(async () => {
        await remove(option.id)
    }, [remove, option.id])

    /**
     * Set the entry as in edit mode.
     */
    const setAsEditing = useCallback(() => {
        toggleInEditMode(option.id, true);
    }, [toggleInEditMode, option.id])

    /**
     * Rolls back the values of the form to its initial state.
     */
    const rollbackValues = useCallback((form) => {
        toggleInEditMode(option.id, false);
        form.reset(initialValues);
        form.resetFieldState('title');
        rollback?.();
    }, [initialValues, option.id, toggleInEditMode, rollback]);

    /**
     * Updates the entry by calling the appropriate callback in the props.
     */
    const updateEntry = useCallback(async (values, form) => {
        const normalized = Utils.normalizeIndustryPropertyPredefinedOptionContent(
            typeId,
            values?.title?.toString?.() ?? '',
        );
        if (!normalized.valid) {
            return;
        }
        if (normalized.apiContent === option.title?.toString()) {
            return toggleInEditMode(option.id, false);
        }
        const changes = {
            id: option.id,
            title: normalized.apiContent,
        };
        const createNextRow = addNextOption.current;
        await update(changes, createNextRow);
        addNextOption.current = false;
        if (createNextRow) {
            form.reset(initialValues);
            form.resetFieldState('title');
            setTimeout(() => {
                inputRef.current?.focus();
            }, 300)
        }
    }, [option.title, option.id, typeId, update, toggleInEditMode, initialValues])

    /**
     * If the user presses enter, and the new option is enabled, then add the next option.
     */
    const onFormKeyDown = useCallback((e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            if (e.target.closest(`#${id}`)) {
                if (newOption) {
                    addNextOption.current = true;
                }
                formRef.current?.submit()
            }
        }
    }, [newOption, id])

    const textFieldType = useMemo(() => {
        switch (typeId) {
            case ProductTechnicalPropertyTypeIds.number:
                return 'number';
            case ProductTechnicalPropertyTypeIds.decimal:
            case ProductTechnicalPropertyTypeIds.money:
                return 'decimal';
            case ProductTechnicalPropertyTypeIds.fraction:
                return 'text';
            case ProductTechnicalPropertyTypeIds.text:
            default:
                return 'text';
        }
    }, [typeId])

    return (
        <>
            <Card variant={'outlined'} id={id}>
                <Form
                    onSubmit={updateEntry}
                    validate={validate}
                    initialValues={initialValues}
                    formProps={{
                        noValidate: true,
                        action: undefined,
                        onKeyDownCapture: onFormKeyDown
                    }}
                >
                    {({form}) => {
                        formRef.current = form;
                        return (
                            <Grid container spacing={2} marginInline={0} marginBlock={0}>
                                <Grid xs paddingInline={0}>
                                    <MuiRFF.TextField
                                        inputRef={inputRef}
                                        fullWidth
                                        size={'small'}
                                        name={"title"}
                                        label={"Content"}
                                        type={textFieldType}
                                        disabled={option.removing || option.editing}
                                        required={required.content}
                                        className={classnames({'show-as-text': !option.inEditMode || readonly})}
                                        {...(option.inEditMode && typeId === ProductTechnicalPropertyTypeIds.fraction) ? {
                                            helperText: "Enter the fraction in the format of '2' or '1/2' or '2-1/2'",
                                        } : {}}
                                    />
                                </Grid>
                                <Grid xs={'auto'}>
                                    {
                                        !option.inEditMode && !readonly &&
                                        <Box sx={{display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 0.5}}>
                                            <IconButton
                                                disabled={option.removing}
                                                size={'small'}
                                                onClick={setAsEditing}
                                                color={'primary'}
                                                sx={{backgroundColor: 'background.paper'}}
                                            >
                                                <EditOutlined/>
                                            </IconButton>
                                            {
                                                removable &&
                                                <IconButton
                                                    onClick={removeEntry}
                                                    disabled={option.removing}
                                                    color={'error'}
                                                    size={'small'}
                                                    sx={{backgroundColor: 'background.paper'}}
                                                >
                                                    {
                                                        option.removing
                                                            ? <CircularProgress size={15} color={'primary'}/>
                                                            : <DeleteOutlined/>
                                                    }
                                                </IconButton>
                                            }
                                        </Box>
                                    }
                                    {
                                        option.inEditMode &&
                                        <SaveOrRollback
                                            show
                                            column={false}
                                            showText={false}
                                            onSave={() => {
                                                if (newOption) {
                                                    addNextOption.current = true;
                                                }
                                                form.submit()
                                            }}
                                            onRollback={() => rollbackValues(form)}
                                            loading={option.editing}
                                        />
                                    }
                                </Grid>
                            </Grid>
                        )
                    }}
                </Form>
            </Card>
        </>
    );
})

export default UpsertProductTechnicalPropertyDialog;
